import { useMutation, useQuery, UseQueryResult } from "@tanstack/react-query";
import React, { useState } from "react";
import { CustomOnboardingRedirectionParams, Page } from "../../activities/AppPage";
import { API } from "../../API";
import useTrainingCenterState from "../../hooks/useTrainingCenterState";
import { isLoading, loading } from "../../Loading";
import {
  CaregiverCertification,
  HRApplicationForm,
  HRSection,
  TrainingCenterBundleDueDateId,
} from "../../messages";
import { getLocalStorageItem, setLocalStorageItem } from "../../storage_utils";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import { AppCaregiver } from "../../system/AppState/AppCaregiverState";
import { TrainingCenterCaregiverBundle } from "../../system/AppState/AppStateTrainingCenter";
import { ChatRoom } from "../../system/AppState/ChatState";
import { getLanguage, translate } from "../../translate";
import ErrorScaffold from "../ErrorScaffold";
import AppModal from "../Generic/AppModal/AppModal";
import { isDefined } from "../Generic/IsDefined";
import LoadingScaffold from "../LoadingScaffold";
import useOnboardingOrientation from "../OnboardingSection/hooks/useOnboardingOrientation";
import { TrainingCenterLanguagePreference } from "../TrainingCenter/TrainingCenterLanguageLookup";
import useModal from "../useModal";
import OnboardingScreen from "./OnboardingScreen";
import useToast from "../../hooks/useToaster";
import { useSocket } from "../../hooks/useSocket";
interface Props {
  api: API;
  caregiver: AppCaregiver;
  appState: AppState;
  languagePreference: TrainingCenterLanguagePreference;
  customDirectionParams: CustomOnboardingRedirectionParams | null;
  handleNavigateReplaceBundle: (bundleDueDateId: TrainingCenterBundleDueDateId) => void;
  getApplicationBanner: API["getApplicationBanner"];
  getCountNewMessages: (chatRoom: ChatRoom) => number;
  onClickGoBack: () => void;
  onClickBundle: (bundleDueDateId: TrainingCenterBundleDueDateId) => void;
  onClickChat: () => void;
  onRequestNavigate: (page: Page) => void;
  onRequestNavigateToBundle: (bundle: TrainingCenterCaregiverBundle) => void;
}

const OnboardingActivity = (props: Props) => {
  const [customRedirect, setCustomRedirect] = useState(props.customDirectionParams);
  const caregiverApplicationQuery: UseQueryResult<HRApplicationForm, unknown> = useQuery({
    queryKey: [
      "caregiver-application",
      customRedirect,
      props.caregiver.agenciesCaregiverCertifications,
    ],
    queryFn: () =>
      props.api.getCaregiverApplication(
        getCaregiverApplicationCertification(props.caregiver.agenciesCaregiverCertifications)
      ),
    onSuccess: (data) => {
      if (customRedirect !== null && caregiverLanguageQuery.status === "success") {
        const section = data.sections.find((x) => x.data.id === customRedirect.sectionId);
        if (section !== undefined) {
          setCustomRedirect(null);
          return props.onRequestNavigate({
            type: "OnboardingSectionActivity",
            sections: data.sections,
            section,
            caregiverApplicationQuery: caregiverApplicationQuery,
            language: caregiverLanguageQuery.data,
            pageKey: section.data.id.toString(),
            customDirectionParams: customRedirect,
          });
        }
      }
    },
  });

  useSocket({
    eventName: "OnboardingStageUpdated",
    onEvent: (_payload) => caregiverApplicationQuery.refetch(),
  });

  const caregiverLanguageQuery = useQuery({
    queryKey: ["caregiver-language"],
    queryFn: getLanguage,
  });
  const sendWelcomeChatMessageMutation = useMutation(() => props.api.sendWelcomeChatMessage());
  const trainingCenterState = useTrainingCenterState(props.appState);
  const chatAgencyMemberIdQuery = useQuery({
    queryKey: ["application_banner"],
    queryFn: props.getApplicationBanner,
  });
  const orientation = useOnboardingOrientation({
    appState: props.appState,
    orientationBundleDueDateId:
      caregiverApplicationQuery.data?.orientationBundleDueDateId ?? loading(),
    onRequestNavigate: props.onRequestNavigate,
  });

  const toast = useToast();
  const modal = useModal();

  const handleClickLockedSection = () => {
    toast.create({
      key: "setionLocked",
      variant: "primary",
      timeout: 4000,
      message: translate(STRINGS.SECTION_LOCKED_PROMPT),
    });
  };

  const sendWelcomeChatMessage = async () => {
    const key = "welcomeChatMessageSent";
    const exists = isDefined(await getLocalStorageItem(key));

    if (exists) {
      return;
    }

    await sendWelcomeChatMessageMutation.mutateAsync();

    setLocalStorageItem(key, "true");
  };

  const handleClickGoBack = () => {
    sendWelcomeChatMessage();
    props.onClickGoBack();
  };

  if (
    caregiverApplicationQuery.status === "error" ||
    caregiverLanguageQuery.status === "error" ||
    chatAgencyMemberIdQuery.status === "error"
  ) {
    return (
      <ErrorScaffold
        title={translate(STRINGS.ONBOARDING)}
        message={translate(STRINGS.SOMETHING_WENT_WRONG)}
        onClickRetry={caregiverApplicationQuery.refetch}
        onClickGoBack={handleClickGoBack}
      />
    );
  }

  if (
    caregiverApplicationQuery.status === "loading" ||
    caregiverLanguageQuery.status === "loading" ||
    chatAgencyMemberIdQuery.status === "loading" ||
    isLoading(trainingCenterState)
  ) {
    return (
      <LoadingScaffold title={translate(STRINGS.ONBOARDING)} onClickGoBack={handleClickGoBack} />
    );
  }

  const data = caregiverApplicationQuery.data;
  const language = caregiverLanguageQuery.data;

  const handleClickSection = (
    section: HRSection,
    sections: HRSection[],
    customDirectionParams: CustomOnboardingRedirectionParams | null
  ) => {
    return props.onRequestNavigate({
      type: "OnboardingSectionActivity",
      sections,
      section,
      caregiverApplicationQuery,
      language: language,
      pageKey: section.data.id.toString(),
      customDirectionParams,
    });
  };

  return (
    <>
      <OnboardingScreen
        sections={data.sections}
        language={language}
        bundle={orientation.bundle}
        languagePreference={props.languagePreference}
        showOnboardingMessage={data.showMessageOnVideoThumbnail}
        isCriminalBackgroundCompliant={data.isCriminalBackgroundCompliant}
        isInterviewCompliant={data.isInterviewCompliant}
        onClickChat={props.onClickChat}
        onClickGoBack={handleClickGoBack}
        onClickLockedSection={handleClickLockedSection}
        onClickSection={handleClickSection}
        onClickWatchOrientation={orientation.navigateToBundle}
      />
      <AppModal {...modal.modalProps} />
    </>
  );
};

function getCaregiverApplicationCertification(
  agenciesCaregiverCertifications: AppCaregiver["agenciesCaregiverCertifications"]
) {
  const onboardingAgenciesCaregiverCertifications = agenciesCaregiverCertifications.find(
    (certification) => certification.isOnboardingAgency
  );

  if (
    (onboardingAgenciesCaregiverCertifications?.certifications.includes(
      CaregiverCertification.wrap("PCA")
    ) ??
      false) &&
    !(
      onboardingAgenciesCaregiverCertifications?.certifications.includes(
        CaregiverCertification.wrap("HHA")
      ) ?? false
    )
  ) {
    return CaregiverCertification.wrap("PCA");
  } else if (
    onboardingAgenciesCaregiverCertifications?.certifications.includes(
      CaregiverCertification.wrap("RN")
    ) ??
    false
  ) {
    return CaregiverCertification.wrap("RN");
  }

  return CaregiverCertification.wrap("HHA");
}

export default OnboardingActivity;
